import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadSession } from 'utilities/storefrontSession';
import * as Cart from 'utilities/storefrontCart';
import { adoptStoredCart } from 'utilities/storefrontCartSync';
import { MAX, brandPath, navLinkStyle } from './styles';
import { useStorefrontFrame } from '../StorefrontGate';
import CookieConsent from './CookieConsent';

/**
 * Sticky top nav used on every storefront page.
 *
 * Tracks the brand's cart count (via the Cart store's subscribe API and
 * cross-tab `storage` events) and whether the shopper has a session for
 * THIS brand (per-brand session — a customer of brand A staying signed
 * in shouldn't affect brand B). Optional announcement bar on top, full
 * mobile drawer below the breakpoint.
 */
export default function StorefrontNav({ brand }) {
  const [cartCount, setCartCount] = useState(0);
  const [signedIn, setSignedIn] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const accent = brand?.brand_color || '#422AFB';
  // The brand's own menu, when the storefront is running on their domain.
  // Without it the shop was an island — its own nav and no route back to the
  // pages the owner built.
  const globalLinks = useStorefrontFrame()?.frame?.header?.links || [];

  useEffect(() => {
    if (!brand?._id) return undefined;
    const refresh = () => {
      const items = Cart.loadCart(brand._id);
      setCartCount(Cart.cartTotals(items).count);
      setSignedIn(Boolean(loadSession(brand._id)?.token));
    };
    refresh();
    // A signed-in shopper arriving on a device with no local basket gets the
    // one they left on another. Only when there is nothing here: somebody
    // with items in front of them must not have them replaced by a cart from
    // somewhere else, which is the version of this that loses a sale rather
    // than saving one.
    adoptStoredCart(brand._id);
    const unsub = Cart.subscribe(refresh);
    const onStorage = (e) => {
      if (e.key && e.key.startsWith('tpo_')) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => { unsub && unsub(); window.removeEventListener('storage', onStorage); };
  }, [brand?._id]);

  // Close the mobile sheet when the user clicks a nav link (the next
  // route mounts a fresh page; the open sheet would be stale).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onClick = (e) => {
      const a = e.target?.closest?.('a');
      if (a) setMobileOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [mobileOpen]);

  if (!brand) return null;

  const linkList = (className) => (
    <nav className={className}>
      {globalLinks.filter((l) => l.label && l.url).map((l, i) => (
        <Link key={`g${i}`} to={l.url} style={navLinkStyle}>{l.label}</Link>
      ))}
      <Link to={brandPath(brand, '/shop')} style={navLinkStyle}>Shop</Link>
      {signedIn && (
        <Link to={brandPath(brand, '/wishlist')} style={navLinkStyle}>Wishlist</Link>
      )}
      <Link to={brandPath(brand, '/cart')} style={navLinkStyle}>
        Cart{cartCount > 0 && (
          <span style={{
            marginLeft: 6, display: 'inline-block',
            padding: '1px 8px', borderRadius: 999,
            background: accent, color: '#fff',
            fontSize: 11, fontWeight: 700,
          }}>{cartCount}</span>
        )}
      </Link>
      <Link to={brandPath(brand, '/contact')} style={navLinkStyle}>Contact</Link>
      {brand.customer_login_enabled && (
        signedIn
          ? <Link to={brandPath(brand, '/account')} style={navLinkStyle}>Account</Link>
          : <Link to={brandPath(brand, '/login')} style={navLinkStyle}>Sign in</Link>
      )}
    </nav>
  );

  const announcementText = (brand?.announcement_text || '').trim();
  const showAnnouncement = brand?.announcement_enabled && announcementText.length > 0;
  const annLink = (brand?.announcement_link || '').trim();

  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10 }}>
      {showAnnouncement && (
        <div style={{
          background: accent, color: '#fff', textAlign: 'center',
          padding: '6px 16px', fontSize: 13, fontWeight: 600,
        }}>
          {annLink ? (
            <a
              href={annLink}
              target={/^https?:\/\//.test(annLink) ? '_blank' : undefined}
              rel='noopener noreferrer'
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              {announcementText}
            </a>
          ) : (
            <span>{announcementText}</span>
          )}
        </div>
      )}
      <style>{`
        .tpo-nav-desktop { display: flex; align-items: center; gap: 18px; }
        .tpo-nav-toggle  { display: none; }
        @media (max-width: 720px) {
          .tpo-nav-desktop { display: none; }
          .tpo-nav-toggle  { display: inline-flex; }
        }
        .tpo-nav-mobile {
          display: flex; flex-direction: column; gap: 6px;
          padding: 12px 20px 18px;
          border-top: 1px solid #e5e7eb;
          background: #fff;
        }
        .tpo-nav-mobile a {
          padding: 10px 4px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
      `}</style>
      <div style={{
        ...MAX,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px',
      }}>
        <Link to={brandPath(brand, '')} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
          {brand.logo_upload_path ? (
            <img src={brand.logo_upload_path} alt='' style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: accent, color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 14,
            }}>
              {(brand.name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{brand.name}</div>
        </Link>
        {linkList('tpo-nav-desktop')}
        <button
          type='button'
          className='tpo-nav-toggle'
          onClick={() => setMobileOpen((v) => !v)}
          aria-label='Toggle menu'
          aria-expanded={mobileOpen}
          style={{
            background: 'transparent', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
            position: 'relative',
          }}
        >
          <span style={{ display: 'block', width: 18, height: 2, background: '#0f172a', marginBottom: 4 }} />
          <span style={{ display: 'block', width: 18, height: 2, background: '#0f172a', marginBottom: 4 }} />
          <span style={{ display: 'block', width: 18, height: 2, background: '#0f172a' }} />
          {cartCount > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: accent, color: '#fff',
              borderRadius: 999, padding: '0 6px',
              fontSize: 10, fontWeight: 700,
            }}>{cartCount}</span>
          )}
        </button>
      </div>
      {mobileOpen && linkList('tpo-nav-mobile')}
      <CookieConsent brand={brand} />
    </div>
  );
}
